<template>
    <div>
        <!-- Inline mode: show content directly without button/collapse wrapper -->
        <template v-if="inline">
            <div v-if="loading" class="mt-2 text-muted">
                <i class="fa fa-spinner fa-spin"></i> Loading ingredients...
            </div>
            <template v-else>
                <b-row v-if="presets.length > 0" class="mt-2 mb-2">
                    <b-col cols="6">
                        <label><small><strong>Billing Preset:</strong></small></label>
                        <b-form-select v-model="selectedPresetId" size="sm" :disabled="isBilled || saving" @change="onPresetChange">
                            <option :value="null">Default Formula</option>
                            <option v-for="p in presets" :key="p.id" :value="p.id">
                                {{ p.name }}{{ p.isDefault ? ' (Default)' : '' }}
                            </option>
                        </b-form-select>
                        <small v-if="isBilled" class="text-muted"><i class="fa fa-lock"></i> Preset locked while prescription is billed</small>
                        <small v-if="saving" class="text-muted"><i class="fa fa-spinner fa-spin"></i> Saving...</small>
                    </b-col>
                </b-row>
                <div v-if="loadingPresetIngredients" class="mt-2 text-muted">
                    <i class="fa fa-spinner fa-spin"></i> Loading preset ingredients...
                </div>
                <b-table-simple v-else-if="displayIngredients.length" small bordered class="mt-2 mb-0">
                    <b-thead>
                        <b-tr>
                            <b-th>Drug Name</b-th>
                            <b-th>NDC</b-th>
                            <b-th>Quantity</b-th>
                            <b-th>UOM</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="(ingredient, idx) in displayIngredients" :key="idx">
                            <b-td>{{ ingredient.drugName || '' }}</b-td>
                            <b-td>{{ ingredient.ndc || '' }}</b-td>
                            <b-td>{{ ingredient.quantity }}</b-td>
                            <b-td>{{ ingredient.unitOfMeasure }}</b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>
                <div v-else-if="loadError" class="mt-2 text-danger">
                    <i class="fa fa-exclamation-triangle"></i> Failed to load ingredients.
                </div>
            </template>
        </template>

        <!-- Standard mode: button + collapse -->
        <template v-else>
            <b-row class="mt-2">
                <b-col>
                    <b-button variant="outline-info" size="sm" v-b-toggle="'compound-ingredients-' + drugId">
                        <i class="fa fa-flask"></i> Ingredients ({{ displayIngredients.length || ingredients.length }})
                        <i class="fa fa-chevron-down ml-1"></i>
                    </b-button>
                    <b-collapse :id="'compound-ingredients-' + drugId" @show="onCollapseShow">
                        <div v-if="loading" class="mt-2 text-muted">
                            <i class="fa fa-spinner fa-spin"></i> Loading ingredients...
                        </div>
                        <template v-else>
                            <!-- Preset Selector -->
                            <b-row v-if="presets.length > 0" class="mt-2 mb-2">
                                <b-col cols="6">
                                    <label><small><strong>Billing Preset:</strong></small></label>
                                    <b-form-select
                                        v-model="selectedPresetId"
                                        size="sm"
                                        :disabled="isBilled || saving"
                                        @change="onPresetChange"
                                    >
                                        <option :value="null">Default Formula</option>
                                        <option
                                            v-for="p in presets"
                                            :key="p.id"
                                            :value="p.id"
                                        >
                                            {{ p.name }}{{ p.isDefault ? ' (Default)' : '' }}
                                        </option>
                                    </b-form-select>
                                    <small v-if="isBilled" class="text-muted">
                                        <i class="fa fa-lock"></i> Preset locked while prescription is billed
                                    </small>
                                    <small v-if="saving" class="text-muted">
                                        <i class="fa fa-spinner fa-spin"></i> Saving...
                                    </small>
                                </b-col>
                            </b-row>

                            <!-- Ingredient Table -->
                            <div v-if="loadingPresetIngredients" class="mt-2 text-muted">
                                <i class="fa fa-spinner fa-spin"></i> Loading preset ingredients...
                            </div>
                            <b-table-simple v-else-if="displayIngredients.length" small bordered class="mt-2 mb-0">
                                <b-thead>
                                    <b-tr>
                                        <b-th>Drug Name</b-th>
                                        <b-th>NDC</b-th>
                                        <b-th>Quantity</b-th>
                                        <b-th>UOM</b-th>
                                    </b-tr>
                                </b-thead>
                                <b-tbody>
                                    <b-tr v-for="(ingredient, idx) in displayIngredients" :key="idx">
                                        <b-td>{{ ingredient.drugName || '' }}</b-td>
                                        <b-td>{{ ingredient.ndc || '' }}</b-td>
                                        <b-td>{{ ingredient.quantity }}</b-td>
                                        <b-td>{{ ingredient.unitOfMeasure }}</b-td>
                                    </b-tr>
                                </b-tbody>
                            </b-table-simple>

                            <div v-else-if="loadError" class="mt-2 text-danger">
                                <i class="fa fa-exclamation-triangle"></i> Failed to load ingredients.
                            </div>
                        </template>
                    </b-collapse>
                </b-col>
            </b-row>
            <b-row v-if="hasViewPermission" class="mt-1">
                <b-col>
                    <router-link :to="{ name: 'Compound-Drug', params: { id: String(drugId) } }" target="_blank">
                        <i class="fa fa-external-link"></i> View/Edit Drug
                    </router-link>
                </b-col>
            </b-row>
        </template>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { CompoundIngredients, CompoundPreset, CompoundPresetIngredient } from '@/models/Drug/CompoundDrug';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import Axios from 'axios';

interface ResolvedIngredient {
    drugName: string;
    ndc: string;
    quantity: number;
    unitOfMeasure: string;
}

@Component({ name: 'CompoundIngredientsDisplay' })
export default class CompoundIngredientsDisplay extends Vue {
    @Prop({ required: true }) private ingredients!: CompoundIngredients[];
    @Prop({ required: true }) private drugId!: number;
    @Prop({ required: false, default: null }) private storeId!: number | null;
    @Prop({ required: false, default: null }) private rxNumber!: number | null;
    @Prop({ required: false, default: null }) private rfNumber!: number | null;
    @Prop({ required: false, default: null }) private compoundPresetId!: number | null;
    @Prop({ required: false, default: false }) private isBilled!: boolean;
    @Prop({ required: false, default: false }) private inline!: boolean;

    private resolvedIngredients: CompoundIngredients[] = [];
    private resolvedPresetIngredients: ResolvedIngredient[] = [];
    private presetIngredientCache: Record<number, ResolvedIngredient[]> = {};
    private presets: CompoundPreset[] = [];
    private selectedPresetId: number | null = null;
    private loading = false;
    private loadingPresetIngredients = false;
    private saving = false;
    private loadError = false;
    private loaded = false;

    get hasViewPermission(): boolean {
        return this.$permissions.some(
            (p: any) => p.permissionCodeName.toLowerCase() === 'Maintenance/Compound Drug/Read'.toLowerCase()
        );
    }

    get hasPrescriptionContext(): boolean {
        return this.storeId !== null && this.rxNumber !== null && this.rfNumber !== null;
    }

    get prescriptionId(): string {
        return `${this.storeId}-${this.rxNumber}-${this.rfNumber}`;
    }

    get displayIngredients(): ResolvedIngredient[] {
        if (this.selectedPresetId !== null && this.resolvedPresetIngredients.length > 0) {
            return this.resolvedPresetIngredients;
        }
        if (this.selectedPresetId !== null && this.loadingPresetIngredients) {
            return [];
        }
        return this.resolvedIngredients;
    }

    @Watch('compoundPresetId', { immediate: true })
    onCompoundPresetIdChanged(val: number | null) {
        this.selectedPresetId = val;
        if (val !== null && this.loaded) {
            this.resolvePresetIngredients(val);
        }
    }

    mounted() {
        if (this.inline) this.fetchData();
    }

    private onCollapseShow() {
        if (this.loaded) return;
        this.fetchData();
    }

    private async fetchData() {
        this.loading = true;
        this.loadError = false;

        try {
            const requests: Promise<any>[] = [
                Axios.get<CompoundIngredients[]>(`/Compound/${this.drugId}/ingredients`),
                Axios.get<CompoundPreset[]>(`/Compound/${this.drugId}/presets`)
            ];

            const [ingredientsRes, presetsRes] = await Promise.all(requests);
            this.resolvedIngredients = ingredientsRes.data;
            this.presets = presetsRes.data || [];
            this.loaded = true;

            // If a preset is already selected, resolve its ingredients
            if (this.selectedPresetId !== null) {
                this.resolvePresetIngredients(this.selectedPresetId);
            }
        } catch {
            this.loadError = true;
        } finally {
            this.loading = false;
        }
    }

    private async resolvePresetIngredients(presetId: number) {
        // Check cache first
        if (this.presetIngredientCache[presetId]) {
            this.resolvedPresetIngredients = this.presetIngredientCache[presetId];
            return;
        }

        const preset = this.presets.find(p => p.id === presetId);
        if (!preset?.ingredients?.length) {
            this.resolvedPresetIngredients = [];
            return;
        }

        this.loadingPresetIngredients = true;
        try {
            const resolved = await Promise.all(
                preset.ingredients.map(pi => this.fetchDrugDetails(pi))
            );
            this.presetIngredientCache[presetId] = resolved;
            // Only apply if still the selected preset
            if (this.selectedPresetId === presetId) {
                this.resolvedPresetIngredients = resolved;
            }
        } catch (error) {
            console.warn('Error resolving preset ingredients:', error);
            // Fallback to what we have from the preset data
            this.resolvedPresetIngredients = preset.ingredients.map(pi => ({
                drugName: pi.drugName || 'Unknown',
                ndc: '',
                quantity: pi.quantity,
                unitOfMeasure: pi.unitOfMeasure
            }));
        } finally {
            this.loadingPresetIngredients = false;
        }
    }

    private async fetchDrugDetails(pi: CompoundPresetIngredient): Promise<ResolvedIngredient> {
        try {
            const response = await Axios.get(`/api/Drug/${pi.drugID}`, {
                params: { drugSource: pi.drugSource }
            });
            const drug = response.data;
            return {
                drugName: drug?.productNameShort || drug?.name || pi.drugName || 'Unknown',
                ndc: drug?.ndc || '',
                quantity: pi.quantity,
                unitOfMeasure: pi.unitOfMeasure
            };
        } catch {
            return {
                drugName: pi.drugName || 'Unknown',
                ndc: '',
                quantity: pi.quantity,
                unitOfMeasure: pi.unitOfMeasure
            };
        }
    }

    private async onPresetChange(presetId: number | null) {
        // Clear previous preset ingredients immediately
        this.resolvedPresetIngredients = [];

        // Resolve new preset's ingredients
        if (presetId !== null) {
            this.resolvePresetIngredients(presetId);
        }

        if (!this.hasPrescriptionContext) return;

        this.saving = true;
        try {
            await Axios.post(
                `/api/Prescription/${this.prescriptionId}/compound-preset`,
                presetId,
                { headers: { 'Content-Type': 'application/json' } }
            );
            this.$emit('preset-changed', presetId);
        } catch (error) {
            console.error('Error updating compound preset:', error);
            this.$notification(NotificationOptions.error('Failed to update compound preset'));
            // Revert selection
            this.selectedPresetId = this.compoundPresetId;
            this.resolvedPresetIngredients = [];
            if (this.compoundPresetId !== null) {
                this.resolvePresetIngredients(this.compoundPresetId);
            }
        } finally {
            this.saving = false;
        }
    }
}
</script>
